import { useState, useRef } from "react";

interface VideoThumbnailProps {
  videoSrc: string;
  posterSrc: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
}

/**
 * VideoThumbnail - Shows a static image and plays MP4 video on hover
 * This is much more performant than using animated GIFs (4.8MB GIF -> ~100KB MP4)
 */
export function VideoThumbnail({
  videoSrc,
  posterSrc,
  alt,
  className = "",
  width = 340,
  height = 213,
}: VideoThumbnailProps) {
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleMouseEnter = () => {
    setIsHovering(true);
    videoRef.current?.play();
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Static poster image - shown by default */}
      <img
        src={posterSrc}
        alt={alt}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isHovering ? "opacity-0" : "opacity-100"
        }`}
      />
      
      {/* Video - shown on hover */}
      <video
        ref={videoRef}
        src={videoSrc}
        muted
        loop
        playsInline
        preload="none"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
          isHovering ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}
